import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError, AxiosResponse, AxiosRequestConfig } from "axios";
import { NetworkCode, ResponseModel, ErrorModel } from "./types";
import { getKMSConfig } from "./kmsConfig";
import { registerKMSRequestInterceptor, registerKMSResponseInterceptor, initUserKMS } from "./kmsClient";

/**
 * 扩展 AxiosRequestConfig，增加自定义配置项
 */
export interface CustomAxiosRequestConfig<D = unknown> extends AxiosRequestConfig<D> {
  /**
   * 是否跳过 Authorization header 的添加
   * 用于私有化环境等不需要认证的场景
   * @default false
   */
  skipAuth?: boolean;
  /**
   * 是否跳过 response 的 code 验证
   * 用于某些接口（如上传到第三方存储）返回 httpStatus 200 但无需判断 response code 的场景
   * @default false
   */
  skipResponseValidation?: boolean;
}

const tokenUrls = [
  'login/enc/token',
  'login/token',
]

interface IHttpRequestOptions {
  baseURL: string;
  storageKey?: string;
  timeout?: number;
  tokenInvalidCb?: () => void;
  refreshTokenCb?: (oldRefreshToken: string) => Promise<string>;
}

export default class HttpRequest {

  private service: AxiosInstance;
  private storageKey: string;
  private refreshStorageKey: string;
  private timeStorageKey: string;
  private expiresInStorageKey: string;
  private tokenInvalidCb?: () => void;
  private token: string | null;
  private refreshToken: string | null;
  private refreshTokenCb?: (oldRefreshToken: string) => Promise<string>;
  private expiresIn: string | null;
  private tokenTime: string | null;
  private refreshing: boolean = false;

  constructor(options: IHttpRequestOptions) {
    const { baseURL, storageKey = "token", timeout = 30 * 1000, tokenInvalidCb, refreshTokenCb } = options;
    this.service = axios.create({
      baseURL,
      timeout,
    });
    this.storageKey = storageKey;
    this.tokenInvalidCb = tokenInvalidCb;
    this.refreshTokenCb = refreshTokenCb;
    this.refreshStorageKey = `${this.storageKey}_refresh`;
    this.expiresInStorageKey = `${this.storageKey}_expires_in`;
    this.timeStorageKey = `${this.storageKey}_time`;
    this.token = this.getToken();
    this.refreshToken = this.getRefreshToken();
    this.expiresIn = this.getExpiresIn() || '1209600';
    this.tokenTime = this.getTokenTime();

    // 初始化 KMS 用户信息
    if (this.token) {
      initUserKMS(this.token);
    }

    // 注册拦截器（顺序很重要）
    // 1. 注册 KMS request 拦截器（前置注册）
    this.interceptorsKMSRequest();
    // 2. 先注册所有业务 request 拦截器
    this.interceptorsTokenRequest();
    this.interceptorsLogRequest();

    // 3. 注册 KMS response 拦截器（前置注册）
    this.interceptorsKMSResponse();
    // 4. 最后注册所有业务 response 拦截器
    this.interceptorsLogResponse();
    this.handleResponse();
  }

  /**
  * Token 请求拦截器
  */
  private interceptorsTokenRequest() {
    this.service.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        // 自动刷新 token，提前 2 天刷新
        try {
          const now = new Date().getTime();
          // expiresIn 是秒为单位，全部转毫秒
          if (now - Number(this.tokenTime) >= Number(this.expiresIn) * 1000 - 2 * 60 * 60 * 24 * 1000) {
            if (this.refreshTokenCb && this.refreshToken) {
              if (!this.refreshing) {
                this.refreshing = true;
                await this.refreshTokenCb(this.refreshToken);
                console.log('finish refresh token');
                this.refreshing = false;
              }
            }
          }
        } catch (err: any) {
          console.error('refreshToken error', err);
          this.tokenInvalidCb && this.tokenInvalidCb();
        }

        // 排除掉 token 相关的接口
        if (!tokenUrls.some(url => config.url?.includes(url))) {
          // 等待 token 刷新完成再发出请求
          await this.handleWaitRefreshToken(config);
        }

        if (!(config.data instanceof FormData)) {
          config.headers.accept = "application/json";
          config.headers["content-type"] = "application/json";
        }
        // 支持 skipAuth 配置，用于私有化环境等不需要认证的场景
        const customConfig = config as CustomAxiosRequestConfig;
        if (!customConfig.skipAuth) {
          config.headers["Authorization"] = `Bearer ${this.token}`;
        }
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      },
      {
        synchronous: false,
        runWhen: () => {
          return true;
        },
      }
    );
  }

  private handleWaitRefreshToken(config: InternalAxiosRequestConfig) {
    return new Promise((resolve) => {
      if (this.refreshing) {
        console.log(config.url, 'refreshing token');
        setTimeout(() => {
          return this.handleWaitRefreshToken(config).then(resolve);
        }, 300);
      } else {
        resolve(true);
      }
    })
  }

  /**
   * 日志请求拦截器
   */
  private interceptorsLogRequest() {
    this.service.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => config,
      (error: AxiosError) => {
        console.error(error);
        return Promise.reject(error);
      }
    );
  }

  /**
 * KMS 请求拦截器
 * 使用 KMSAxios 自动处理加密
 * 注意：必须在所有业务 request 拦截器注册完成后调用
 */
  private interceptorsKMSRequest() {
    const kmsConfig = getKMSConfig();

    if (!kmsConfig.enabled) {
      console.log('[KMS] KMS 配置未启用，跳过注册 KMS 请求拦截器');
      return;
    }

    // 注册请求拦截器并保存 ID（需要在所有业务 request 拦截器之后）
    const userRequestId = registerKMSRequestInterceptor(this.service, 'user');
    if (userRequestId !== null && kmsConfig.verbose) {
      console.log('[KMS] User 请求拦截器已注册，ID:', userRequestId);
    }

    const guestRequestId = registerKMSRequestInterceptor(this.service, 'guest');
    if (guestRequestId !== null && kmsConfig.verbose) {
      console.log('[KMS] Guest 请求拦截器已注册，ID:', guestRequestId);
    }
  }

  /**
   * KMS 响应拦截器
   * 使用 KMSAxios 自动处理解密
   * 注意：必须在所有业务 response 拦截器注册之前调用
   */
  private interceptorsKMSResponse() {
    const kmsConfig = getKMSConfig();

    if (!kmsConfig.enabled) {
      console.log('[KMS] KMS 配置未启用，跳过注册 KMS 响应拦截器');
      return;
    }

    // 注册响应拦截器并保存 ID（需要在所有业务 response 拦截器之前）
    const guestResponseId = registerKMSResponseInterceptor(this.service, 'guest');
    if (guestResponseId !== null && kmsConfig.verbose) {
      console.log('[KMS] Guest 响应拦截器已注册，ID:', guestResponseId);
    }

    const userResponseId = registerKMSResponseInterceptor(this.service, 'user');
    if (userResponseId !== null && kmsConfig.verbose) {
      console.log('[KMS] User 响应拦截器已注册，ID:', userResponseId);
    }
  }

  /**
   * 日志响应拦截器
   */
  private interceptorsLogResponse() {
    this.service.interceptors.response.use(
      (response: AxiosResponse<ResponseModel>) => response,
      (error: AxiosError) => {
        console.groupCollapsed(`[Response Error ${error.config?.method} ${error.response?.status}] ${error.config?.url}`);
        console.error(error.response?.data);
        console.error(error);
        console.groupEnd();
        return Promise.reject(error);
      }
    );
  }

  private handleResponse() {
    this.service.interceptors.response.use(
      async (response: AxiosResponse<ResponseModel>): Promise<AxiosResponse['data']> => {
        // 检查是否跳过 response 验证
        const customConfig = response.config as CustomAxiosRequestConfig;
        if (customConfig.skipResponseValidation) {
          // 如果配置了跳过验证，直接返回 response，不进行 code 判断
          return response.data;
        }

        const { data } = response;
        const { code } = data;
        if (code == undefined) {
          const err: ErrorModel = { errCode: -1, errMsg: data.message };
          return Promise.reject(err);
        }

        if (code === NetworkCode.success || code === 0) {
          return response.data;
        } else if (code === NetworkCode.invalid) {
          this.handleError({
            errCode: response.data.code,
            errMsg: response.data.message,
          });
        } else if (code === NetworkCode.doing) {
          // 这种情况稍微delay一下，再重试一次
          // 避免频繁请求重试
          await this.delay(10 * 1000);
          return this.service(response.config);
        } else {
          const err: ErrorModel = { errCode: code, errMsg: data.message };
          return Promise.reject(err);
        }
      },
      (error: AxiosError<any>) => {
        return this.handleError({
          errCode: error.response?.data.code || error.code || error.status || 500,
          errMsg: error.response?.data.message || error.message,
        });
      }
    );
  }

  private handleError = ({ errCode, errMsg }: any) => {
    const err: ErrorModel = {
      errCode,
      errMsg,
    };
    const failCb = () => Promise.reject(err);
    if (errCode === NetworkCode.invalid && this.tokenInvalidCb) {
      this.clearToken();
      this.tokenInvalidCb();
    }
    return failCb();
  }

  get<T = unknown>(api: string, config?: CustomAxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.service.get(api, { ...config });
  }

  post<T = unknown>(api: string, data: unknown, config?: CustomAxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.service.post(api, data, config);
  }

  put<T = unknown>(api: string, data: unknown, config?: CustomAxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.service.put(api, data, config);
  }

  patch<T = unknown>(api: string, data?: unknown, config?: CustomAxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.service.patch(api, data, config);
  }

  delete<T = unknown>(api: string, config?: CustomAxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.service.delete(api, config);
  }

  clearToken() {
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem(this.refreshStorageKey);
    localStorage.removeItem(this.expiresInStorageKey);
    localStorage.removeItem(this.timeStorageKey);
    this.token = null;
    this.refreshToken = null;
    this.expiresIn = null;
    this.tokenTime = null;
  }

  setToken(token: string, refreshToken: string, expiresIn: string | number) {
    localStorage.setItem(this.storageKey, token);
    this.token = token;
    localStorage.setItem(this.refreshStorageKey, refreshToken);
    this.refreshToken = refreshToken;
    localStorage.setItem(this.expiresInStorageKey, expiresIn.toString());
    this.expiresIn = expiresIn.toString();
    const now = new Date().getTime();
    localStorage.setItem(this.timeStorageKey, now.toString());
    this.tokenTime = now.toString();

    // 更新 KMS 用户信息
    initUserKMS(token);
  }

  getToken() {
    return localStorage.getItem(this.storageKey);
  }

  getRefreshToken() {
    return localStorage.getItem(this.refreshStorageKey);
  }

  getExpiresIn() {
    return localStorage.getItem(this.expiresInStorageKey);
  }

  getTokenTime() {
    return localStorage.getItem(this.timeStorageKey);
  }

  setHttpHeader(key: string, value: string) {
    this.service.defaults.headers.common[key] = value;
  }

  getHttpHeader(key: string) {
    return this.service.defaults.headers.common[key];
  }

  delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

}
